import React, { useState, useEffect } from 'react'
import * as styles from './styles.module.css'
import { useMutation, useQuery } from 'react-query'
import CircularProgress from '@material-ui/core/CircularProgress'
import SnackbarComponent from '../../../../../Snackbar/index'
import ModalSettings from '../../modal/ModalSettings'
import BaseAlert from '../../../../../Snackbar/NewImplementations/BaseAlert/BaseAlert'
import { getSubscriptionRenewDate } from '../../../../../../Api'
import { getLatestSubscriptionByType } from './subscriptionUtils'

import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { findPlanForType } from './find'

function BoxComponent ({
  getAccount,
  updateAccountPlan,
  title,
  plans,
  creditsKey,
  planTypeKey
}) {
  const { data: dataAccount, isLoading } = useQuery('settings', getAccount, {
    onError: error => {
      setSnackMessage({
        type: 'error',
        myMessage: error.message,
        update: Date.now()
      })
    }
  })

  const [snackMessage, setSnackMessage] = useState({ type: 'error' })
  const [onLoadingPlansChange, SetOnLoadingPlansChange] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [currentPlanId, setCurrentPlanId] = useState(null)
  const [availableCredits, setAvailableCredits] = useState(0)
  const [isYearly, setIsYearly] = useState(false) // New state for pricing model
  const customerId = dataAccount?.data?.customer_id

  const { data: renewData } = useQuery(
    ['renew-date', customerId],
    () => getSubscriptionRenewDate(customerId),
    { enabled: Boolean(customerId) }
  )

  const [alert, setAlert] = useState({
    open: false,
    title: '',
    body: '',
    color: undefined,
    actions: []
  })

  const creditsUsed = dataAccount?.data?.[creditsKey]

  const [profileUpdatePlan] = useMutation(
    planState => updateAccountPlan(planState),
    { onSuccess: handleSuccessUpdatePlan, onError: handleErrorUpdatePlan }
  )

  function handleSuccessUpdatePlan (data) {
    const { status, data: payload } = data

    // handle payment-source error only
    if (status >= 400) {
      const errMsg = payload?.errors?.[0] || ''
      if (errMsg.includes('Payment source is required for plan:')) {
        setAlert({
          open: true,
          title: 'Payment required',
          body: 'A valid payment source is required to change this subscription plan. Please add a payment method in your payment information.',
          color: '#EF3688',
          actions: [
            {
              label: 'Ok',
              onClick: () => setAlert(a => ({ ...a, open: false }))
            }
          ]
        })
      }
      SetOnLoadingPlansChange(false)
      return
    }

    // success path
    setAlert({
      open: true,
      title: 'Subscription updated',
      body: 'Refresh the page to see the changes.',
      color: '#48B966',
      actions: [
        {
          label: 'Ok',
          onClick: () => {
            setAlert(a => ({ ...a, open: false }))
            setTimeout(() => window.location.reload(), 300)
          }
        }
      ]
    })
  }

  function handleErrorUpdatePlan (error) {
    const errMsg = error.message || 'An unexpected error occurred'

    // Generic error alert
    setAlert({
      open: true,
      title: 'Error',
      body: errMsg,
      color: '#EF3688',
      actions: [
        {
          label: 'Ok',
          onClick: () => setAlert(a => ({ ...a, open: false }))
        }
      ]
    })

    SetOnLoadingPlansChange(false)
  }

  // const handleClickOpen = () => {
  //   setOpen(true)
  // }
  const handleClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (isLoading || !dataAccount?.data?.plans) return

    const plansList = dataAccount.data.plans
    const dataPlan = findPlanForType(plansList, planTypeKey)

    if (dataPlan) {
      const idLower = String(dataPlan.id || '').toLowerCase()
      setSelectedPlan(idLower)
      setCurrentPlanId(idLower)
      const periodUnit = String(
        dataPlan.period_unit || dataPlan.periodUnit || ''
      ).toLowerCase()
      setIsYearly(
        periodUnit === 'year' ||
          idLower.includes('annual') ||
          idLower.includes('60k')
      )

      const allotted = Number(dataPlan.credits_allotted ?? 0)
      const used = Number(creditsUsed ?? 0)
      setAvailableCredits(Math.max(0, allotted - used))
    } else {
      // No plan for this data type → show 0 credits and no selection
      setSelectedPlan(null)
      setCurrentPlanId(null)
      setIsYearly(false)
      setAvailableCredits(0)
    }
  }, [dataAccount, isLoading, creditsUsed, planTypeKey])

  const getBoxStyle = planId => {
    return planId === selectedPlan
      ? styles.SelectedBoxProperty
      : styles.BoxProperty
  }

  const handlePlanClick = planId => {
    setSelectedPlan(planId === selectedPlan ? null : planId)
  }

  const onClickUpdatePlans = () => {
    SetOnLoadingPlansChange(true)
    profileUpdatePlan({ plan_ids: [selectedPlan] })
  }

  const isPlanUnchanged = selectedPlan === currentPlanId

  const YearlyAndMonthlyUnlimitedPlans = [
    'business-unlimited-monthly',
    'people-unlimited-monthly',
    'product-unlimited-monthly',
    'property-unlimited-monthly',
    'people_unlimited_monthly',
    'people-testunlimitedpeople-60k',
    'people-unlimitedpeople-60k'
  ]

  const filteredPlans =
    planTypeKey === 'people'
      ? isYearly
        ? plans
            // For People in yearly mode, filter out free (trial) plans and the monthly unlimited plans,
            // then switch to yearly pricing
            .filter(
              plan =>
                plan.yearlyPrice !== 'Free' &&
                !plan.id.toLowerCase().includes('trial') &&
                !YearlyAndMonthlyUnlimitedPlans.includes(plan.id)
            )
            .map(plan => ({
              ...plan,
              id: plan.yearlyId,
              price: plan.yearlyPrice
            }))
        : // For People in monthly mode, allow trial plans and filter out only the monthly unlimited ones.
          plans.filter(
            plan => !YearlyAndMonthlyUnlimitedPlans.includes(plan.id)
          )
      : isYearly
      ? plans
          .filter(plan => plan.yearlyPrice !== 'Free')
          .map(plan => ({
            ...plan,
            id: plan.yearlyId,
            price: plan.yearlyPrice
          }))
      : plans.filter(plan => !YearlyAndMonthlyUnlimitedPlans.includes(plan.id))

  // Subscriptions
  const allSubscriptions = renewData?.data?.data ?? []
  const latestSubscriptionsByType = React.useMemo(
    () => getLatestSubscriptionByType(allSubscriptions),
    [allSubscriptions]
  )

  const subscriptionForCurrentType = latestSubscriptionsByType[planTypeKey]

  const renewDateFormatted = subscriptionForCurrentType?.current_period_end
    ? new Date(
        subscriptionForCurrentType.current_period_end * 1000
      ).toLocaleString()
    : null

  const renewTooltip = renewDateFormatted
    ? `Next renewal: ${renewDateFormatted}`
    : 'No active subscription for this data type'

  // short date for the badge
  const renewDateShort = subscriptionForCurrentType?.current_period_end
    ? new Date(
        subscriptionForCurrentType.current_period_end * 1000
      ).toLocaleDateString()
    : null

  return (
    <section className={styles.PropertyDataSettingsContainer}>
      <div className={styles.FirstContainerSettings}>
        <h1 className={styles.TitlePropertyDataSettings}>{title}</h1>
        <div className={styles.PricingToggle}>
          <span
            className={!isYearly ? styles.Active : styles.Inactive}
            onClick={() => setIsYearly(false)}
          >
            Monthly
          </span>
          <span
            className={isYearly ? styles.Active : styles.Inactive}
            onClick={() => setIsYearly(true)}
          >
            Annual
          </span>
        </div>
      </div>
      <div className={styles.SecondContainerSettings}>
        <div className={styles.TopMetaRow}>
          {/* Left: credits */}
          <div className={styles.CreditsWrapper}>
            <span className={styles.CreditsAvailableNumber}>
              {availableCredits}
            </span>
            <span className={styles.CreditsAvailableSpan}>
              credits available
            </span>
          </div>

          {/* Right: renew date with tooltip */}
          <Tooltip
            title={renewTooltip}
            placement='top'
            slotProps={{
              popper: {
                sx: {
                  '& .MuiTooltip-tooltip': {
                    fontSize: '13px',
                    padding: '8px'
                  }
                }
              }
            }}
            arrow
          >
            <div className={styles.RenewDateWrapper}>
              <span className={styles.RenewDateLabel}>Renew date</span>
              <span className={styles.RenewDateBadge}>
                {renewDateShort ?? '—'}
              </span>
              <InfoOutlinedIcon className={styles.RenewDateIcon} />
            </div>
          </Tooltip>
        </div>
      </div>

      {filteredPlans.map(plan => (
        <div
          key={plan.id}
          className={getBoxStyle(plan.id)}
          onClick={() => handlePlanClick(plan.id)}
        >
          <div className={styles.BoxPropertyDirection}>
            <span
              className={
                plan.id === selectedPlan
                  ? styles.SelectedSpanTitleBoxProperty
                  : styles.SpanTitleBoxProperty
              }
            >
              {plan.name}
            </span>
            <span
              className={
                plan.id === selectedPlan
                  ? styles.SelectSpanBoxProperty
                  : styles.SpanBoxProperty
              }
            >
              {plan.records}
            </span>
          </div>
          <div className={styles.BoxPropertySpacing}>
            <div>
              <span
                style={{ display: 'block' }}
                className={
                  plan.id === selectedPlan
                    ? styles.SelectSpanBoxProperty
                    : styles.SpanBoxProperty
                }
              >
                {plan.price}
              </span>
              <span
                className={
                  plan.id === selectedPlan
                    ? styles.SelectSpanBoxProperty
                    : styles.SpanBoxProperty
                }
              >
                {plan.id.includes('trial')
                  ? 'for 2 weeks'
                  : `per ${isYearly ? 'year' : 'month'}`}
              </span>
            </div>
            <div>
              <span
                className={
                  plan.id === selectedPlan
                    ? styles.SelectSpanBoxProperty
                    : styles.SpanBoxProperty
                }
              >
                {selectedPlan === plan.id ? 'Selected' : 'Select'}
              </span>
            </div>
          </div>
        </div>
      ))}
      {onLoadingPlansChange ? (
        <button className={styles.spinnerDisabled}>
          <CircularProgress
            style={{ width: '25px', height: '30px', color: 'white' }}
          />
        </button>
      ) : (
        <button
          disabled={
            onLoadingPlansChange || isPlanUnchanged || selectedPlan === null
          }
          onClick={onClickUpdatePlans}
          className={styles.buttonSettingsUpdate}
        >
          Update
        </button>
      )}
      <ModalSettings open={open} onClose={handleClose} />
      <SnackbarComponent snackData={snackMessage} />

      <BaseAlert
        open={alert.open}
        title={alert.title}
        body={alert.body}
        style={{ width: '320px' }}
        color={alert.color}
        actions={alert.actions}
        onClose={() => {
          setAlert(a => ({ ...a, open: false }))
          setTimeout(() => window.location.reload(), 300)
        }}
      />
    </section>
  )
}

export default BoxComponent
